import { CepingScanPayload, decodeCepingScan } from "@/utils/ceping-scan";

type AlertOptions = {
  dangerouslyUseHTMLString?: boolean;
  confirmButtonText?: string;
};

type MessageHost = {
  error?: (msg: string) => void;
  success?: (msg: string) => void;
  warning?: (msg: string) => void;
};

type AlertHost = {
  $alert: (message: string, title: string, options?: AlertOptions) => Promise<any>;
  $message?: MessageHost;
};

type ConfirmCallback = (payload: CepingScanPayload) => Promise<void> | void;

export class HomeCepingScanListener {
  private scanBuffer = "";
  private lastCharTime = 0;
  private scanFinalizeTimer: number | null = null;
  private isScanDialogShowing = false;
  private started = false;

  private readonly keydownHandler = (e: KeyboardEvent) => this.handleScanInput(e);

  constructor(
    private readonly host: AlertHost,
    private readonly onConfirm?: ConfirmCallback
  ) {}

  public start() {
    if (this.started) return;
    this.started = true;
    window.addEventListener("keydown", this.keydownHandler, true);
  }

  public stop() {
    if (!this.started) return;
    this.started = false;
    window.removeEventListener("keydown", this.keydownHandler, true);
    this.clearFinalizeTimer();
    this.scanBuffer = "";
    this.isScanDialogShowing = false;
  }

  private handleScanInput(e: KeyboardEvent) {
    if (this.isScanDialogShowing) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    const target = e.target as HTMLElement | null;
    const isTypingElement =
      target &&
      (target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.getAttribute("contenteditable") === "true");
    if (isTypingElement) return;

    const now = Date.now();
    if (now - this.lastCharTime > 100) {
      this.scanBuffer = "";
    }
    this.lastCharTime = now;

    if (e.key === "Shift") return;

    if (e.key === "#" || e.key === "Enter" || e.key === "NumpadEnter") {
      e.preventDefault();
      e.stopPropagation();
      this.finalizeScanBuffer();
      return;
    }

    if (e.key.length === 1 && !e.ctrlKey && !e.altKey && !e.metaKey) {
      this.scanBuffer += e.key;
      this.scheduleFinalizeScan();
    }
  }

  private scheduleFinalizeScan() {
    this.clearFinalizeTimer();
    // 某些扫码枪不会追加结束符，短暂静默后自动判定结束
    this.scanFinalizeTimer = window.setTimeout(() => {
      this.finalizeScanBuffer();
    }, 120);
  }

  private clearFinalizeTimer() {
    if (this.scanFinalizeTimer !== null) {
      window.clearTimeout(this.scanFinalizeTimer);
      this.scanFinalizeTimer = null;
    }
  }

  private finalizeScanBuffer() {
    this.clearFinalizeTimer();
    const scanRaw = (this.scanBuffer || "").trim();
    this.scanBuffer = "";
    if (!this.isLikelyScanData(scanRaw)) return;
    this.handleScanResult(scanRaw);
  }

  private isLikelyScanData(value: string) {
    if (!value || value.length < 16) return false;
    if (value.includes("$")) return true;
    return /^[A-Za-z0-9+/_=-]{20,}$/.test(value);
  }

  private handleScanResult(scanRaw: string) {
    const result = decodeCepingScan(scanRaw);

    if (!result.payload) {
      this.isScanDialogShowing = true;
      this.host
        .$alert(
          `原始内容：${this.escapeHtml(result.raw)}<br/>解码结果：${this.escapeHtml(
            result.decoded || "（空）"
          )}`,
          "扫码内容解析失败",
          {
            dangerouslyUseHTMLString: true,
            confirmButtonText: "确定",
          }
        )
        .then(() => this.releaseDialogLockLater())
        .catch(() => this.releaseDialogLockLater());
      return;
    }

    const payload = result.payload;
    const sourceLabel = payload.sourceType === "2" ? "云端患者ID" : "来源类型";
    const genderLine = payload.gender
      ? `<br/>性别：${this.escapeHtml(payload.gender)}`
      : "";
    const birthdayLine = payload.birthday
      ? `<br/>出生日期：${this.escapeHtml(payload.birthday)}`
      : "";
    const heightLine = payload.height
      ? `<br/>身高：${this.escapeHtml(payload.height)}`
      : "";
    const weightLine = payload.weight
      ? `<br/>体重：${this.escapeHtml(payload.weight)}`
      : "";

    this.isScanDialogShowing = true;
    this.host
      .$alert(
        `${sourceLabel}：${this.escapeHtml(payload.sourceType)}<br/>患者名称：${this.escapeHtml(
          payload.patientName
        )}<br/>身份证号：${this.escapeHtml(payload.idCard)}${genderLine}${birthdayLine}${heightLine}${weightLine}<br/>答题答案：${this.escapeHtml(
          payload.answers
        )}`,
        "扫码结果",
        {
          dangerouslyUseHTMLString: true,
          confirmButtonText: "确定",
        }
      )
      .then(async () => {
        try {
          if (this.onConfirm) {
            await this.onConfirm(payload);
          }
        } catch (error) {
          this.host.$message?.error?.("扫码数据处理失败，请重试");
        } finally {
          this.releaseDialogLockLater();
        }
      })
      .catch(() => this.releaseDialogLockLater());
  }

  private releaseDialogLockLater() {
    setTimeout(() => {
      this.isScanDialogShowing = false;
    }, 300);
  }

  private escapeHtml(value: string) {
    return value
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }
}
